import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const followUpConsult: ContentBlock<"procedure"> = {
  id: makeId("proc-follow-up-consult"),
  kind: "procedure",
  tags: ["clinic-visit", "both-partners", "practical"],
  title: "Follow-Up Consultation",
  subtitle: "Review results and adjust your fertility plan with your RE",
  body: {
    procedureName: "Follow-Up Consultation",
    category: "consultation",
    description:
      "A return visit with your reproductive endocrinologist to review completed test results, discuss findings, and refine or adjust your treatment plan. This appointment is where major decisions about next steps are often made.",
    howItWorks:
      "The RE will walk through your lab results, imaging findings, and any procedures completed since your last visit. They will explain what the data means for your fertility picture and may update your diagnosis. Together, you will discuss treatment options, this could range from timed intercourse or IUI to IVF, or additional testing if the picture is incomplete. If you are between cycles, the doctor may review what went well and what could be changed for the next attempt. Most follow-ups are 20-30 minutes and can often be done via telehealth if no physical exam is needed.",
    duration: "20-30 minutes",
    costRange: { low: 150, high: 400 },
    currency: "USD",
    prepInstructions: [
      "Review your test results beforehand if they are available through a patient portal",
      "Write down questions about anything you did not understand in your results",
      "Note any new symptoms or changes since your last visit",
      "If switching protocols, have your prior medication list ready for reference",
    ],
    whatToExpect: [
      "A review of all completed test results and what they mean",
      "Discussion of treatment recommendations and alternatives",
      "Opportunity to ask questions and voice concerns",
      "Updated timeline and next steps",
      "Possible new lab orders or procedure scheduling",
      "Bring insurance card or payment method",
      "Bring your list of questions",
      "Bring a notebook or phone to take notes, there is often a lot of information",
      "Bring your partner if treatment decisions are being discussed",
    ],
    forWhom: "both",
    frBody: {
      procedureName: "Consultation de suivi",
      description:
        "Consultation de suivi avec votre médecin de la reproduction pour examiner les résultats des examens réalisés, en discuter et ajuster votre plan de traitement. C'est souvent à ce rendez-vous que sont prises les grandes décisions concernant la suite.",
      howItWorks:
        "Le médecin reprend l'ensemble de vos résultats biologiques, d'imagerie et les éventuelles procédures réalisées depuis la dernière visite. Il vous explique ce que ces données signifient pour votre projet et peut actualiser le diagnostic. Ensemble, vous discutez des options thérapeutiques : rapports programmés, IIU (insémination intra-utérine), FIV (fécondation in vitro), ou bilans complémentaires si le tableau est incomplet. Si vous êtes entre deux cycles, le médecin reprend ce qui a bien fonctionné et ce qui peut être adapté pour la tentative suivante. La plupart des consultations de suivi durent 20 à 30 minutes et peuvent souvent se faire en téléconsultation si aucun examen clinique n'est nécessaire.",
      duration: "20 à 30 minutes",
      prepInstructions: [
        "Consultez vos résultats à l'avance s'ils sont disponibles dans un portail patient",
        "Notez les questions sur ce que vous n'avez pas compris dans vos résultats",
        "Indiquez les éventuels symptômes ou changements depuis la dernière visite",
        "Si vous changez de protocole, ayez votre liste de traitements antérieurs à portée de main",
      ],
      whatToExpect: [
        "Reprise des résultats d'examens et de leur signification",
        "Discussion des recommandations thérapeutiques et des alternatives",
        "Possibilité de poser des questions et d'exprimer vos préoccupations",
        "Calendrier mis à jour et prochaines étapes",
        "Éventuelles nouvelles ordonnances d'examens ou programmation de gestes",
        "Apportez votre carte Vitale/mutuelle ou un moyen de paiement",
        "Apportez votre liste de questions",
        "Apportez un carnet ou un téléphone pour prendre des notes : il y a souvent beaucoup d'informations",
        "Venez avec votre partenaire si des décisions de traitement sont à prendre",
      ],
    },
  },
  sources: [
    {
      title: "Optimizing natural fertility: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2022,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/optimizing-natural-fertility-a-committee-opinion-2021/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(followUpConsult);
